/* eslint-disable react-hooks/set-state-in-effect */
/**
 * NoticeBoard — floating overlay that watches the event feed and shows
 * short animated notifications for the "wow" moments:
 *   · capture of a NEW species (first time in the pokédex)
 *   · badge earned by a trainer
 *   · Pokémon evolution (level, happiness, stone, trade)
 *   · Pokémon level-up (aggregated multi-level events)
 *   · League win
 *
 * Non-invasive: reads `/api/events?limit=5` every 4s, uses `sessionStorage`
 * to know the last seen event id (so a page refresh doesn't spam animations).
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { spriteUrl } from "@/lib/api";

const LAST_SEEN_KEY = "noticeboard-last-seen";
const POLL_INTERVAL_MS = 4000;
const NOTIFIED_TYPES = ["capture", "badge_win", "evolution", "pokemon_level_up", "league_win"];

export default function NoticeBoard() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const seenRef = useRef(sessionStorage.getItem(LAST_SEEN_KEY) || null);

  // Poll new events
  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/events?limit=8");
      if (!Array.isArray(data) || !data.length) return;
      // Events are returned newest-first. Reverse to process chronologically.
      const events = [...data].reverse();
      let latestId = seenRef.current;
      const fresh = [];
      let firstLoad = seenRef.current === null;
      for (const e of events) {
        if (seenRef.current && e.id === seenRef.current) {
          continue; // skip the last-seen and everything before
        }
        latestId = e.id;
        if (firstLoad) continue; // don't spam animations on the very first mount
        if (NOTIFIED_TYPES.includes(e.type) && isNoteworthy(e)) fresh.push(e);
      }
      if (latestId && latestId !== seenRef.current) {
        seenRef.current = latestId;
        sessionStorage.setItem(LAST_SEEN_KEY, latestId);
      }
      if (fresh.length) setQueue((q) => [...q, ...fresh].slice(-6));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    poll();
    const t = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [user, poll]);

  // Show one notice at a time, dequeue after its animation completes
  useEffect(() => {
    if (current || !queue.length) return;
    const next = queue[0];
    setQueue((q) => q.slice(1));
    setCurrent(next);
    const dur = durationFor(next.type);
    const t = setTimeout(() => setCurrent(null), dur);
    return () => clearTimeout(t);
  }, [queue, current]);

  if (!current) return null;
  return <Notice event={current}/>;
}

function isNoteworthy(e) {
  // Filter to reduce noise:
  //   capture: only NEW species (not already caught by prof)
  //   pokemon_level_up: only if a "count" > 1 or the metadata carries it
  if (e.type === "capture") {
    return e.meta?.is_new_species !== false; // undefined = assume yes
  }
  if (e.type === "pokemon_level_up") {
    return (e.meta?.count || 0) >= 1;
  }
  return true;
}

function durationFor(type) {
  switch (type) {
    case "badge_win": return 2700;
    case "league_win": return 3000;
    case "evolution": return 1000;
    case "pokemon_level_up": return 1300;
    case "capture": return 4300;
    default: return 3000;
  }
}

/* ================ Individual notice renderers ================ */
function Notice({ event }) {
  switch (event.type) {
    case "capture":            return <CaptureToast event={event}/>;
    case "badge_win":          return <BadgeStamp event={event}/>;
    case "evolution":          return <EvolutionHalo event={event}/>;
    case "pokemon_level_up":   return <LevelStars/>;
    case "league_win":         return <BadgeStamp event={event} label="MAÎTRE DE LA LIGUE"/>;
    default: return null;
  }
}

function CaptureToast({ event }) {
  // Stack multiple toasts vertically
  const pid = event.meta?.pokemon_id;
  const name = extractPokemonName(event.message);
  const isShiny = event.meta?.is_shiny;
  return (
    <div className="notice-toast" data-testid="notice-capture" style={{ top: 90 }}>
      <div className="flex items-center gap-3">
        {pid && (
          <img
            src={isShiny ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${pid}.png` : spriteUrl(pid)}
            alt={name}
            className="pixelated w-12 h-12 shrink-0"
          />
        )}
        <div className="min-w-0">
          <div className="overline text-[9px] text-[color:var(--region-accent-ink)]">
            {isShiny ? "Nouvelle espèce · CHROMATIQUE" : "Nouvelle espèce !"}
          </div>
          <div className="font-serif-display text-xl leading-tight truncate">
            {pid ? `#${String(pid).padStart(3, "0")} ${name}` : name}
          </div>
          <div className="text-[10px] font-mono text-[#7A7A7A] truncate">
            {event.trainer_name}
          </div>
        </div>
      </div>
    </div>
  );
}

function BadgeStamp({ event, label = "BADGE OBTENU" }) {
  return (
    <div className="badge-stamp" data-testid="notice-badge">
      <div>{label}</div>
      <div className="text-[11px] mt-1 text-center opacity-75 tracking-widest">
        {event.trainer_name}
      </div>
    </div>
  );
}

function EvolutionHalo({ event }) {
  const pid = event.meta?.pokemon_id;
  return (
    <>
      <div className="evo-halo" data-testid="notice-evolution"/>
      {pid && (
        <div style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 215, pointerEvents: "none",
          animation: "evo-halo 900ms ease-out both",
        }}>
          <img
            src={event.meta?.is_shiny
              ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${pid}.png`
              : spriteUrl(pid)}
            alt=""
            className="pixelated"
            style={{ width: 96, height: 96 }}
          />
        </div>
      )}
    </>
  );
}

function LevelStars() {
  return (
    <>
      <span className="level-star" style={{ "--dx": "-40px" }} data-testid="notice-levelup">⭐</span>
      <span className="level-star" style={{ "--dx": "0px", animationDelay: "80ms" }}>⭐</span>
      <span className="level-star" style={{ "--dx": "40px", animationDelay: "160ms" }}>⭐</span>
    </>
  );
}

/* Extract the pokémon name from a capture message like
   "Nathan Simon a capturé un Chenipan sauvage ! 🎉" */
function extractPokemonName(msg) {
  const m = /capturé un ([^\s!]+)/i.exec(msg || "");
  return m ? m[1] : "Pokémon";
}
